import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Download, FileDown, Search } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useBillingReconciliation,
  useCollectionsTrend,
  usePaymentMethodBreakdown,
  usePaymentsList,
  usePaymentsSummary,
} from '@/hooks/use-payments'
import { useTopArrears } from '@/hooks/use-billing'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { downloadEstateReport, type PaymentsReportRange } from '@/lib/payments'
import { getApiErrorMessage } from '@/lib/api'
import { downloadCsv } from '@/lib/csv'
import { cn, formatKES, formatPaymentMethod } from '@/lib/utils'
import type { PaymentMethod } from '@riverpark/types'
import { CollectionsChart, type CollectionsChartPoint } from './collections-chart'
import { PaymentMethodChart } from './payment-method-chart'
import { TopArrearsChart } from './top-arrears-chart'
import { UnmatchedC2BTab } from './unmatched-c2b-tab'

const TOP_ARREARS_LIMIT = 8

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const METHOD_OPTIONS: PaymentMethod[] = [
  'mpesa',
  'cash',
  'bank_transfer',
  'manual_mpesa',
  'credit_note',
  'c2b_mpesa',
]

function downloadPaymentsCsv(
  rows: {
    invoiceNumber: string
    memberName: string
    amount: number
    method: string
    paidAt: number
  }[],
) {
  downloadCsv(
    `riverpark-payments-${new Date().toISOString().slice(0, 10)}.csv`,
    ['Invoice #', 'Member', 'Amount (KES)', 'Method', 'Paid at'],
    rows.map((r) => [
      r.invoiceNumber,
      r.memberName,
      r.amount,
      r.method,
      new Date(r.paidAt * 1000).toISOString(),
    ]),
  )
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function firstOfMonthInputValue(monthsAgo = 0): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1).toISOString().slice(0, 10)
}

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Date-only <input type="date"> values → full-day ISO range for the API's datetime query params. */
function toRange(from: string, to: string): PaymentsReportRange {
  return {
    from: new Date(`${from}T00:00:00.000Z`).toISOString(),
    to: new Date(`${to}T23:59:59.999Z`).toISOString(),
  }
}

function daySpan(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000)
}

type PeriodId = 'this-month' | '3m' | '6m' | '12m' | 'custom'
const PERIOD_OPTIONS: { id: PeriodId; label: string; monthsAgo?: number }[] = [
  { id: 'this-month', label: 'This month', monthsAgo: 0 },
  { id: '3m', label: 'Last 3 months', monthsAgo: 2 },
  { id: '6m', label: 'Last 6 months', monthsAgo: 5 },
  { id: '12m', label: 'Last 12 months', monthsAgo: 11 },
  { id: 'custom', label: 'Custom range' },
]

/** Beyond ~2 months a daily bar per day is unreadable — switch to monthly automatically. */
const DAY_GRANULARITY_MAX_SPAN = 62

type Tab = 'overview' | 'payments' | 'unmatched'
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'payments', label: 'Payments' },
  { id: 'unmatched', label: 'Unmatched Payments' },
]
const VALID_TABS: Tab[] = ['overview', 'payments', 'unmatched']

export function PaymentsWorkspace({ billingPath }: { billingPath: string }) {
  const [tab, setTab] = useTabQueryParam(VALID_TABS, 'overview')
  const [period, setPeriod] = useState<PeriodId>('this-month')
  const [customFrom, setCustomFrom] = useState(firstOfMonthInputValue())
  const [customTo, setCustomTo] = useState(todayInputValue())
  const [utilityTypeId, setUtilityTypeId] = useState('')
  const [method, setMethod] = useState('')
  const [search, setSearch] = useState('')
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const { data: utilityData } = useUtilityTypes(tab !== 'unmatched')
  const utilityTypes = utilityData?.data ?? []

  const selectedPeriod = PERIOD_OPTIONS.find((p) => p.id === period)!
  const from = period === 'custom' ? customFrom : firstOfMonthInputValue(selectedPeriod.monthsAgo)
  const to = period === 'custom' ? customTo : todayInputValue()
  const granularity: 'day' | 'month' =
    daySpan(from, to) <= DAY_GRANULARITY_MAX_SPAN ? 'day' : 'month'

  function changePeriod(id: PeriodId) {
    if (id === 'custom') {
      setCustomFrom(from)
      setCustomTo(to)
    }
    setPeriod(id)
  }

  const range: PaymentsReportRange = {
    ...toRange(from, to),
    ...(utilityTypeId && { utilityTypeId }),
    ...(method && { method: method as PaymentMethod }),
  }
  const isOverview = tab === 'overview'
  const { data: reconData, isLoading: reconLoading } = useBillingReconciliation(range, isOverview)
  const { data: summaryData, isLoading: summaryLoading } = usePaymentsSummary(range, isOverview)
  const { data: trendData, isLoading: trendLoading } = useCollectionsTrend(range, isOverview)
  const { data: paymentsData, isLoading: paymentsLoading } = usePaymentsList(
    range,
    tab === 'payments',
  )
  const { data: methodBreakdownData, isLoading: methodBreakdownLoading } =
    usePaymentMethodBreakdown(range, isOverview)
  const { data: topArrears, isLoading: topArrearsLoading } = useTopArrears(
    TOP_ARREARS_LIMIT,
    isOverview,
  )
  const reconciliation = reconData?.data
  const paymentRows = paymentsData?.data ?? []
  const methodBreakdown = methodBreakdownData?.data ?? []

  const searchQuery = search.trim().toLowerCase()
  const filteredPaymentRows = searchQuery
    ? paymentRows.filter((p) =>
        [p.memberName, p.invoiceNumber, p.mpesaReceiptNumber, p.reference]
          .filter((field): field is string => !!field)
          .some((field) => field.toLowerCase().includes(searchQuery)),
      )
    : paymentRows

  const chartData: CollectionsChartPoint[] =
    granularity === 'day'
      ? (summaryData?.data ?? []).map((row) => ({
          label: row.date,
          totalCollected: row.totalCollected,
          count: row.count,
        }))
      : (trendData?.data ?? []).map((row) => ({
          label: row.month,
          totalCollected: row.totalCollected,
          count: row.count,
        }))
  const chartLoading = granularity === 'day' ? summaryLoading : trendLoading

  async function handleDownloadPdf() {
    setPdfError(null)
    setDownloadingPdf(true)
    try {
      await downloadEstateReport(range.from, range.to)
    } catch (err) {
      setPdfError(getApiErrorMessage(err, 'Failed to generate report'))
    } finally {
      setDownloadingPdf(false)
    }
  }

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title="Payments"
        description="Reconcile M-Pesa, manual payments, and payment history."
      />

      <div className="flex gap-1 border-b border-border-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(tab === 'overview' || tab === 'payments') && (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {tab === 'payments' && (
            <div className="relative sm:max-w-[280px] sm:flex-1">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by member, invoice #, or reference"
                className="h-10 w-full rounded border border-input bg-card pl-9 pr-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              />
            </div>
          )}
          <select
            className={`${SELECT_CLS} sm:max-w-[180px]`}
            value={period}
            onChange={(e) => changePeriod(e.target.value as PeriodId)}
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          {period === 'custom' && (
            <>
              <Input
                label="From"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <Input
                label="To"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          )}
          <select
            className={`${SELECT_CLS} sm:max-w-[200px]`}
            value={utilityTypeId}
            onChange={(e) => setUtilityTypeId(e.target.value)}
          >
            <option value="">All utility types</option>
            {utilityTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            className={`${SELECT_CLS} sm:max-w-[200px]`}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="">All payment methods</option>
            {METHOD_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {formatPaymentMethod(m)}
              </option>
            ))}
          </select>
        </div>
      )}

      {tab === 'overview' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-card border border-border-2 bg-card px-5 py-4">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                Total billed
              </p>
              <p className="mt-2 font-serif text-stat-sm leading-none tracking-tight text-ink">
                {reconLoading ? '—' : formatKES(reconciliation?.totalBilled ?? 0)}
              </p>
              <p className="mt-1.5 font-sans text-xs text-muted-foreground">
                Standard invoices created in range
              </p>
            </div>
            <div className="rounded-card border border-border-2 bg-card px-5 py-4">
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                Total collected
              </p>
              <p className="mt-2 font-serif text-stat-sm leading-none tracking-tight text-status-paid-text">
                {reconLoading ? '—' : formatKES(reconciliation?.totalCollected ?? 0)}
              </p>
              <p className="mt-1.5 font-sans text-xs text-muted-foreground">
                Payments received in range
              </p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <div className="space-y-3">
              <p className="font-sans text-sm font-medium text-ink">
                Collections {granularity === 'day' ? 'by day' : 'by month'}
              </p>
              <CollectionsChart data={chartData} isLoading={chartLoading} />
            </div>
            <div className="space-y-3">
              <p className="font-sans text-sm font-medium text-ink">Collections by method</p>
              <PaymentMethodChart data={methodBreakdown} isLoading={methodBreakdownLoading} />
            </div>
          </div>

          <div className="space-y-3">
            <p className="font-sans text-sm font-medium text-ink">Biggest outstanding balances</p>
            <TopArrearsChart data={topArrears ?? []} isLoading={topArrearsLoading} />
          </div>

          <Link
            to={`${billingPath}?tab=arrears`}
            className="inline-flex items-center gap-1.5 font-sans text-sm text-primary hover:text-green-lt"
          >
            Outstanding arrears by member
            <ArrowRight size={14} />
          </Link>
        </>
      )}

      {tab === 'payments' && (
        <>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={downloadingPdf}
              onClick={() => void handleDownloadPdf()}
            >
              <FileDown size={14} className="mr-1.5" />
              Download PDF
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadPaymentsCsv(filteredPaymentRows)}
            >
              <Download size={14} className="mr-1.5" />
              Export CSV
            </Button>
          </div>
          {pdfError && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {pdfError}
            </p>
          )}

          {paymentsLoading ? (
            <p className="font-sans text-sm text-muted-foreground">Loading…</p>
          ) : filteredPaymentRows.length === 0 ? (
            <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
              {searchQuery ? 'No payments match your search.' : 'No payments in this range.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-border-2">
              <table className="w-full font-sans text-sm">
                <thead>
                  <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Paid at</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPaymentRows.map((p) => (
                    <tr key={p.id} className="border-b border-border-2 last:border-0">
                      <td className="px-4 py-3 font-medium text-ink">{p.memberName}</td>
                      <td className="px-4 py-3 text-ink">{p.invoiceNumber}</td>
                      <td className="px-4 py-3 text-ink">{formatKES(p.amount)}</td>
                      <td className="px-4 py-3 text-ink">{formatPaymentMethod(p.method)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {p.mpesaReceiptNumber ?? p.reference ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-ink">{formatDate(p.paidAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'unmatched' && <UnmatchedC2BTab />}
    </PageShell>
  )
}
